'use client'
import shouldEnablePerformance from '@/utils/shouldEnablePerformance'
import React, { Profiler, ProfilerOnRenderCallback } from 'react'

interface ReactProfilerProps {
  id: string
  children: React.ReactNode
  onRender?: ProfilerOnRenderCallback
}

/**
 * React Profiler component for measuring component performance
 *
 * Usage:
 * <ReactProfiler id="MyComponent">
 *   <MyComponent />
 * </ReactProfiler>
 */
export const ReactProfiler: React.FC<ReactProfilerProps> = ({
  id,
  children,
  onRender,
}) => {
  const enabled = shouldEnablePerformance()
  const handleRender: ProfilerOnRenderCallback = (
    id: string,
    phase: string,
    actualDuration: number,
    baseDuration: number,
    startTime: number,
    commitTime: number
  ) => {
    console.group(`🔍 React Profiler: ${id}`)
    console.log('Phase:', phase)
    console.groupEnd()

    // Call custom onRender if provided
    if (onRender) {
      onRender(
        id,
        phase as 'mount' | 'update' | 'nested-update',
        actualDuration,
        baseDuration,
        startTime,
        commitTime
      )
    }

    // Performance warning for slow renders
    if (actualDuration > 16) {
      // 16ms = 60fps threshold
      console.warn(
        `⚠️ Slow render detected for ${id}: ${actualDuration.toFixed(2)}ms`
      )
    }
  }

  // If profiling is disabled, just render children
  if (!enabled) {
    return <>{children}</>
  }

  return (
    <Profiler id={id} onRender={handleRender}>
      {children}
    </Profiler>
  )
}

export default ReactProfiler
